import { useTranslation } from 'react-i18next';

interface MissingHintProps {
  /** What the picture is still missing, named, in the order it is asked for. */
  items: string[];
  /**
   * Whether this is advice rather than a rule — which is what the drawing is on
   * a photo, where the save asks once and goes ahead (see `isDrawingAdvice`).
   * Then it is said in the amber of something worth doing rather than the red of
   * something in the way, and named as work left rather than as a refusal. A
   * board problem and a scan's climb still cannot be saved without their three
   * roles, and keep the red.
   */
  advice?: boolean;
}

/**
 * What the picture still wants, written across the top of it.
 *
 * The drawing is the one requirement with no field to go red: there is no
 * control that means "a start hold", only the wall itself. So it is said on the
 * wall — and said *always*, not only once the save has been pressed, because a
 * setter looking at a photo of forty holds has no other way of knowing that
 * three of them are what tells this climb from the one crossing it.
 *
 * At the top and in the colour a warning wears, because it is the one thing on
 * this screen that is not going to be noticed by being looked for: the bottom of
 * the picture is where the panel of actions opens on a route, and a grey caption
 * under a wall of holds reads as a caption rather than as something to do. It
 * hangs just under the row of overlay buttons rather than level with them, so it
 * can run the full width of the picture without either covering the other.
 *
 * It goes as the last of them is marked. A caption that stayed to say "nothing
 * missing" would be a line of text earning nothing, over the only part of the
 * screen that is being worked on.
 *
 * Inert (`pointer-events-none`): the picture underneath is drawn on with taps,
 * and a label that swallowed one would take a hold the setter was aiming at.
 */
export function MissingHint({ items, advice = false }: MissingHintProps) {
  const { t } = useTranslation();

  if (items.length === 0) return null;

  return (
    <div
      className="pointer-events-none absolute inset-x-0 top-12 z-10 flex justify-center px-4"
      data-testid="climb-missing-hint"
    >
      {/* The list is put together here rather than interpolated into the
          sentence: the translated part is a clause that ends in a colon, and
          what follows it is names in the page's own order. */}
      <span
        className={`rounded-full px-3 py-1 text-center text-[11px] font-semibold leading-snug text-white shadow-lg backdrop-blur ${
          advice ? 'bg-amber-600/90' : 'bg-red-600/90'
        }`}
      >
        {t(advice ? 'climbEditor.missing.suggestion' : 'climbEditor.missing.hint')}{' '}
        {items.join(', ')}
      </span>
    </div>
  );
}
